import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Container, Row, Col, Button, CardTitle, Modal, FormGroup, Label, Input, Alert
} from "reactstrap";
import api from "../../../../services/api";
import qs from "qs";
import { ConfirmDeleteAlert, DeleteFailedAlert, DeleteSuccessAlert } from "../../../alerts";
import { ComboBox, DropdownBox } from "../../../dropdowns";
import { TooltipButton } from '../../../inputs';
import Loader from "../../../loaders";
import { DefaultColumnFilter } from "../../../react-table/filters";
import { useTable, useExpanded, useFilters, useSortBy, useFlexLayout } from 'react-table';
import Grid from "../../../Grid";
import { IndeterminateCheckbox } from '../../../react-table/controls';

function JobOrderMilestoneMaster(props) {

    const [ data, setData ] = useState([]);
    const [ loading, setLoading ] = useState(false);
    const [ itemInEdit, setItemInEdit ] = useState(null);
    const [ pageNumber, setPageNumber ] = useState(1);
    const [ pageSize, setPageSize ] = useState(10);
    const [ totalRecords, setTotalRecords ] = useState(0);
    const [ deleteAlert, setDeleteAlert ] = useState([]);
    const [ validationMessages, setValidationMessages ] = useState([]);
    const [ filters, setFilters ] = useState([]);

    const defaultSort = useMemo(() => [
        { id: "project", desc: false },
        { id: "location", desc: false }
    ]);

    const [sortBy, setSortBy] = useState(defaultSort);
    const skipPageResetRef = useRef();

    const loadData = useCallback(() => {
        skipPageResetRef.current = true;
        const queryString = qs.stringify({ filters, sortBy }, { allowDots: true });
        setItemInEdit(null);
        setData([]);

        setLoading(true);
        api.get(`/jobordermilestonemaster/${(pageNumber - 1) * pageSize}/${pageSize}${queryString ? `?${queryString}` : ""}`)
            .then(({ data }) => {
                setData(data.data);
                setTotalRecords(data.total);
            }).catch((error) => {
                console.error("error: ", error);
            }).finally(() => setLoading(false));
    }, [filters, sortBy, pageNumber, pageSize]);

    const handleDelete = (item) => {
        const success = () => {
            setDeleteAlert([<DeleteSuccessAlert onConfirm={() => setDeleteAlert([])} />]);
            if (data.length === 1 && pageNumber > 1) {
                setPageNumber(pageNumber - 1);
            } else {
                loadData();
            }
        };
        const failure = () => setDeleteAlert([<DeleteFailedAlert onConfirm={() => setDeleteAlert([])} />]);
        const events = {
            onConfirm: () => api.delete(`/jobordermilestonemaster/${item.jobOrderMilestoneMasterId}`).then(success).catch(failure),
            onCancel: () => setDeleteAlert([])
        }
        setDeleteAlert([<ConfirmDeleteAlert {...events} />])
    }

    const handleSave = useCallback(() => {
        setLoading(true);

        const data = JSON.stringify(itemInEdit);
        const save = itemInEdit.jobOrderMilestoneMasterId > 0 
            ? api.put(`jobordermilestonemaster/${itemInEdit.jobOrderMilestoneMasterId}`, data)
            : api.post("jobordermilestonemaster", data)

        save
            .then(() => {
                loadData();
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [itemInEdit]);

    const handleTextChanged = ({ target: { name, value } }) => {
        setItemInEdit({ ...itemInEdit, [name]: value });
    }

    useEffect(() => {
        loadData();
    }, [pageNumber, pageSize, filters, sortBy]);

    const filterTypes = useMemo(
        () => ({
            text: (rows, id, filterValue) => {
                return rows.filter(row => {
                    const rowValue = row.values[id]
                    return rowValue !== undefined
                        ? String(rowValue).replace(/[/-]/g, "")
                            .toLowerCase()
                            .includes(String(filterValue).replace(/[/-]/g, "").toLowerCase())
                        : true
                })
            },
        }),
        []
    )

    const columns = useMemo(() => {
        return [
            {
                Header: "Name",
                id: "jobOrderMilestoneMasterName",
                accessor: (row) => row.jobOrderMilestoneMasterName,
                filter: "text",
                width: 50,
                disableSortBy: false,
            },
            {
                Header: "Group Owner",
                id: "groupOwner.userGroupName",
                accessor: (row) => row.groupOwner?.userGroupName,
                filter: "text",
                width: 200,
                disableSortBy: false,
            },
            {
                Header: "Require SMS",
                id: "requireSms",
                accessor: (row) => row.requireSms ? "Y" : "N",
                filter: "text",
                width: 50,
                disableSortBy: false,
            },
            {
                Header: "Require Document",
                id: "needDocument",
                accessor: (row) => row.needDocument ? "Y" : "N",
                filter: "text",
                width: 50,
                disableSortBy: false,
            },
            {
                Header: "View Document Template",
                id: "canViewDocumentTemplate",
                accessor: (row) => row.canViewDocumentTemplate ? "Y" : "N",
                filter: "text",
                width: 50,
                disableSortBy: false,
            },
            {
                Header: "Actions",
                id: 'button',
                Cell: ({ row: { original } }) =>
                (
                    <div>
                        <TooltipButton
                            id={`edit_${original.jobOrderMilestoneMasterId}`}
                            title="Edit"
                            className="btn-icon"
                            color="default"
                            size="sm"
                            type="button"
                            onClick={() => {
                                setItemInEdit(original);
                            }}
                        >
                            <i className="fas fa-pencil-alt pt-1"></i>
                        </TooltipButton>
                        <TooltipButton
                            id={`delete_${original.jobOrderMilestoneMasterId}`}
                            title="Delete"
                            className="btn-icon"
                            color="warning"
                            size="sm"
                            type="button"
                            onClick={() => handleDelete(original)}
                        >
                            <i className="fas fa-trash-alt pt-1"></i>
                        </TooltipButton>
                    </div>
                ),
                width: 140,
            },
        ]
    }, []);

    const defaultColumn = useMemo(() => ({
        width: 150,
        Filter: DefaultColumnFilter,
        filter: "text",
        disableSortBy: true,
    }), []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state,
    } = useTable(
        {
            columns,
            data: data,
            initialState: {
                sortBy: defaultSort,
                canSort: true,
            },
            filterTypes,
            defaultColumn,
            manualSortBy: true,
            autoResetPage: !skipPageResetRef.current,
            autoResetExpanded: !skipPageResetRef.current,
            autoResetGroupBy: !skipPageResetRef.current,
            autoResetSelectedRows: !skipPageResetRef.current,
            autoResetSortBy: !skipPageResetRef.current,
            autoResetFilters: !skipPageResetRef.current,
            autoResetRowState: !skipPageResetRef.current,
        },
        useFlexLayout,
        useFilters,
        useSortBy,
        useExpanded
    );

    useEffect(() => {
        setFilters(state.filters);
    }, [state.filters]);

    useEffect(() => {
        setSortBy(state.sortBy);
    }, [state.sortBy]);

    const validate = useCallback(() => {
        const newValidationMessages = [];

        if (!itemInEdit?.jobOrderMilestoneMasterName) {
            newValidationMessages.push("Please enter Milestone Name.");
        }

        if (!itemInEdit?.groupOwnerId) {
            newValidationMessages.push("Please enter Group Owner.");
        }

        if (itemInEdit?.requireSms && !itemInEdit.messagesMasterId) {
            newValidationMessages.push("Please choose Message Template.");
        }
        
        setValidationMessages(newValidationMessages);

        if (newValidationMessages.length)
            return;

        handleSave();
    }, [itemInEdit, handleSave]);
    
    return (
        <>
            {loading && <Loader />}
            {deleteAlert.length > 0 && deleteAlert}
            <Container fluid>
                <Row>
                    <Col xs={12}>
                        <CardTitle>
                            <h4 className="text-center">Job Order Milestone Master</h4>
                        </CardTitle>
                        <TooltipButton
                            id="addnew"
                            title="Add New"
                            className="btn-icon ml-2 mb-2"
                            color="default"
                            size="sm"
                            type="button"
                            onClick={() => {
                                setItemInEdit({});
                            }}
                        >
                            <i className="fas fa-plus pt-1"></i>
                        </TooltipButton>
                        <Grid
                            height='calc(100vh - 460px)'
                            totalRecords={totalRecords}
                            pageSize={pageSize}
                            rows={rows}
                            tableProps={getTableProps()}
                            headerGroups={headerGroups}
                            tableBodyProps={getTableBodyProps()}
                            prepareRow={prepareRow}
                            onPagerChangePage={(pager) => {
                                setPageNumber(pager.currentPage);
                            }}
                            onPagerChangePageSize={(size) => {
                                setPageSize(size);
                            }}
                            needPaging={true}
                        />
                    </Col>
                </Row>
            </Container>
            {
                !!itemInEdit &&
                <Modal
                    isOpen={true}
                    className="modal-lg"
                    modalClassName="db-example-modal-lg"
                >
                    <div
                        className="modal-header"
                    >
                        <h5
                            className="modal-title"
                        >
                            Job Order Milestone Master Editor
                        </h5>
                        <button
                            aria-label="Close"
                            className="close"
                            onClick={() => setItemInEdit(null)}
                            type="button"
                        >
                            <span aria-hidden={true}><i className="fas fa-times-circle" /></span>
                        </button>
                    </div>
                    <div
                        className="modal-body"
                    >
                        <Container>
                        {
                            validationMessages.length > 0 &&
                            <Alert color="warning" className="mb-0 d-flex mx-auto justify-content-between mt-3">
                                <ul className="mb-0">
                                {
                                    validationMessages.map((m, idx) => 
                                        <li key={`app_am_${idx}`}>{m}</li>
                                    )
                                }
                                </ul>
                            </Alert>
                        }
                            <Row>
                                <Col xs={12}>
                                    <FormGroup>
                                        <Label>
                                            Name
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            name="jobOrderMilestoneMasterName"
                                            id="jobOrderMilestoneMasterName"
                                            type="text"
                                            required
                                            value={itemInEdit.jobOrderMilestoneMasterName ?? ""}
                                            onChange={handleTextChanged}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col xs={12}>
                                    <FormGroup>
                                        <Label>
                                            Group Owner
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <ComboBox
                                            endpoint="/jobordermilestonemaster/user-group-list"
                                            isLookup={false}
                                            idField="userGroupId"
                                            valueField="userGroupName"
                                            minLength={0}
                                            selectedItem={itemInEdit.groupOwner}
                                            onChange={(selected) => {
                                                setItemInEdit({ ...itemInEdit, groupOwnerId: selected.userGroupId, groupOwner: selected });
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col xs={12}>
                                    <FormGroup>
                                        <IndeterminateCheckbox
                                            id="isRequired"
                                            type="checkbox"
                                            labelClassName="job-order-control mr-3"
                                            onChange={(e) => {
                                                setItemInEdit({ ...itemInEdit, isRequired: e.target?.checked ?? false });
                                            }}
                                            checked={itemInEdit.isRequired ?? false}
                                            customTitle="Mandatory Milestone"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col xs={12}>
                                    <FormGroup>
                                        <IndeterminateCheckbox
                                            id="needDocument"
                                            type="checkbox"
                                            labelClassName="job-order-control mr-3"
                                            onChange={(e) => {
                                                // console.log(itemInEdit[col.accessor] === col.checkedValue ? col.uncheckedValue : col.checkedValue);
                                                setItemInEdit({ ...itemInEdit, needDocument: e.target?.checked ?? false, canViewDocumentTemplate: !e.target?.checked ? false : itemInEdit.canViewDocumentTemplate });
                                            }}
                                            checked={itemInEdit?.needDocument ?? false}
                                            customTitle="Require Document"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col xs={12}>
                                    <FormGroup>
                                        <IndeterminateCheckbox
                                            id="canViewDocumentTemplate"
                                            type="checkbox"
                                            labelClassName="job-order-control mr-3"
                                            onChange={(e) => {
                                                // console.log(itemInEdit[col.accessor] === col.checkedValue ? col.uncheckedValue : col.checkedValue);
                                                setItemInEdit({ ...itemInEdit, canViewDocumentTemplate: e.target?.checked ?? false });
                                            }}
                                            checked={itemInEdit?.canViewDocumentTemplate ?? false}
                                            customTitle="View Document Template"
                                            disabled={!itemInEdit?.needDocument}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col xs={12}>
                                    <FormGroup>
                                        <IndeterminateCheckbox
                                            id="requireSms"
                                            type="checkbox"
                                            labelClassName="job-order-control mr-3"
                                            onChange={(e) => {
                                                // console.log(itemInEdit[col.accessor] === col.checkedValue ? col.uncheckedValue : col.checkedValue);
                                                setItemInEdit({ ...itemInEdit, requireSms: e.target?.checked ?? false });
                                            }}
                                            checked={itemInEdit.requireSms ?? false}
                                            customTitle="Require SMS"
                                        />
                                    </FormGroup>
                                </Col>
                            {
                                itemInEdit.requireSms ?
                                <Col xs={12}>
                                    <FormGroup>
                                        <Label>
                                            Message Template
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <ComboBox
                                            endpoint="/messagesmaster/list-for-user"
                                            isLookup={false}
                                            idField="lookupId"
                                            valueField="lookupType"
                                            minLength={0}
                                            selectedItem={itemInEdit.messagesMaster}
                                            onChange={(selected) => {
                                                setItemInEdit({ ...itemInEdit, messagesMasterId: selected.lookupId, messagesMaster: selected });
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                :
                                null
                            }
                            </Row>
                        </Container>
                    </div>
                    <div
                        className="modal-footer"
                    >
                        <Button
                            color="secondary"
                            onClick={() => {
                                setItemInEdit(null);
                                setValidationMessages([]);
                            }}
                            type="button"
                        >
                            Close
                        </Button>
                        <Button
                            color="info"
                            onClick={() => validate()}
                            type="button"
                        >
                            Save Changes
                        </Button>
                    </div>
                </Modal>
            }
        </>
    );
}

export default JobOrderMilestoneMaster;